// Orphan accounts (super admins): logins with no organization, a deleted
// organization, or a role that no longer exists. Re-home them or delete them.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserX, Loader2, Trash2, UserCheck, AlertTriangle, Mail, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { lastSignInLabel } from "@/components/UserSortControls";
import { toast } from "sonner";

const DELETE_NOTE = "Deletes the login for good. Audit logs keep the person's name and email; commissions and billing aren't affected.";

function AssignControls({ roles, orgs, value, onChange, testid }) {
  const base = roles.find((r) => r.role_id === value.role)?.base_role;
  const choices = base === "partner" || base === "customer" ? orgs[base] || [] : [];
  return (
    <div className="flex items-center gap-2">
      <Select value={value.role} onValueChange={(role) => onChange({ role, org: "" })}>
        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`${testid}-role`}><SelectValue placeholder="New role…" /></SelectTrigger>
        <SelectContent>{roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name}</SelectItem>)}</SelectContent>
      </Select>
      {choices.length > 0 && (
        <Select value={value.org} onValueChange={(org) => onChange({ ...value, org })}>
          <SelectTrigger className="h-8 w-48 text-xs" data-testid={`${testid}-org`}><SelectValue placeholder={`Pick ${base}…`} /></SelectTrigger>
          <SelectContent>{choices.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
}

const ready = (roles, v) => {
  const base = roles.find((r) => r.role_id === v.role)?.base_role;
  return !!v.role && (base === "staff" || !!v.org);
};

function SuggestionChip({ o }) {
  const s = o.suggestion;
  if (!s) return null;
  return (
    <div className="mt-1 flex max-w-sm items-start gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-800" data-testid={`orphan-suggestion-${o.user_id}`}>
      <Sparkles className="mt-0.5 h-3 w-3 shrink-0" /><span>Suggested: <b>{s.org_name}</b> as {s.role_label} · {s.reason}</span>
    </div>
  );
}

function OrphanRow({ o, roles, orgs, checked, focused, onCheck, onDone, onDelete }) {
  const [v, setV] = useState({ role: o.suggestion?.role || "", org: o.suggestion?.org_id || "" });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (focused) ref.current?.scrollIntoView({ block: "center" }); }, [focused]);
  const isSuggested = !!o.suggestion && v.role === o.suggestion.role && v.org === o.suggestion.org_id;
  const assign = async () => {
    setBusy(true);
    try {
      await api.post(`/user-orphans/${o.user_id}/assign`, { role: v.role, organization_id: v.org });
      toast.success(`${o.name} re-assigned`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assign failed"); }
    finally { setBusy(false); }
  };
  return (
    <tr ref={ref} className={`border-b border-slate-100 align-middle ${focused ? "bg-emerald-50/60 ring-2 ring-inset ring-emerald-300" : ""}`} data-testid={`orphan-row-${o.user_id}`}>
      <td className="py-2 pl-3"><input type="checkbox" checked={checked} onChange={onCheck} className="h-4 w-4 accent-[#206bc4]" data-testid={`orphan-pick-${o.user_id}`} /></td>
      <td className="py-2 pr-3"><div className="text-sm font-medium text-slate-800">{o.name}</div><div className="text-xs text-slate-400">{o.email}</div><SuggestionChip o={o} /></td>
      <td className="py-2 pr-3 text-xs text-amber-700"><div data-testid={`orphan-reason-${o.user_id}`}>{o.reason}</div></td>
      <td className="whitespace-nowrap py-2 pr-3 text-xs text-slate-500">{lastSignInLabel(o.last_login)}</td>
      <td className="py-2 pr-3">
        <div className="flex items-center gap-2">
          <AssignControls roles={roles} orgs={orgs} value={v} onChange={setV} testid={`orphan-assign-${o.user_id}`} />
          <Button size="sm" variant={isSuggested ? "default" : "outline"} className="h-8" disabled={!ready(roles, v) || busy} onClick={assign} data-testid={`orphan-assign-save-${o.user_id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} {isSuggested ? "Apply fix" : "Assign"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-rose-700 hover:bg-rose-50" onClick={() => onDelete([o])} data-testid={`orphan-delete-${o.user_id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function ConfirmDelete({ targets, onCancel, onConfirm, busy }) {
  if (!targets.length) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm" data-testid="orphan-delete-confirm">
      <span className="text-rose-800"><b>Delete {targets.length === 1 ? targets[0].name : `${targets.length} accounts`}?</b> {DELETE_NOTE}</span>
      <div className="flex shrink-0 gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="bg-rose-600 text-white hover:bg-rose-700" disabled={busy} onClick={onConfirm} data-testid="orphan-delete-confirm-btn">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Delete
        </Button>
      </div>
    </div>
  );
}

function AlertNote({ lastSent }) {
  return (
    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400" data-testid="orphan-alert-note">
      <Mail className="h-3 w-3" /> Super admins get an email on Mondays at 8am when new ones appear
      {lastSent ? ` · last sent ${new Date(lastSent).toLocaleDateString()}` : ""}
    </div>
  );
}

// `?orphans=review[&focus=<user_id>]` (links in the Monday email) opens the review table, that row highlighted.
function useReviewDeepLink(items, setOpen, setFocus) {
  const [params, setParams] = useSearchParams();
  const handled = useRef(false);
  const wants = params.get("orphans") === "review";
  useEffect(() => {
    if (!wants || items === null || handled.current) return;
    handled.current = true;
    const focus = params.get("focus");
    if (items.length) { setOpen(true); setFocus(focus || null); }
    if (!items.length) toast.info("No orphan accounts left to review");
    else if (focus && !items.some((o) => o.user_id === focus)) toast.info("That account has already been fixed");
    const next = new URLSearchParams(params);
    next.delete("orphans"); next.delete("focus");
    setParams(next, { replace: true });
  }, [wants, items, params, setParams, setOpen, setFocus]);
}

export function OrphanAccountsCard({ onChanged }) {
  const [items, setItems] = useState(null);
  const [lastSent, setLastSent] = useState(null);
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [orgs, setOrgs] = useState({});
  const [sel, setSel] = useState([]);
  const [bulk, setBulk] = useState({ role: "", org: "" });
  const [pendingDelete, setPendingDelete] = useState([]);
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);

  const load = useCallback(() => api.get("/user-orphans")
    .then((r) => { setItems(r.data.items); setLastSent(r.data.alert_last_sent_at || null); })
    .catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  useReviewDeepLink(items, setOpen, setFocus);
  useEffect(() => {
    if (!open) return;
    api.get("/roles").then((r) => setRoles(r.data.filter((x) => x.role_id !== "super_admin"))).catch(() => {});
    Promise.all([api.get("/organizations", { params: { type: "partner" } }), api.get("/organizations", { params: { type: "customer" } })])
      .then(([p, c]) => setOrgs({ partner: p.data, customer: c.data })).catch(() => {});
  }, [open]);
  const refresh = () => { setSel([]); load(); onChanged?.(); };
  const picked = useMemo(() => (items || []).filter((o) => sel.includes(o.user_id)), [items, sel]);

  const run = async (list, fn, okMsg) => {
    setBusy(true);
    let ok = 0;
    for (const o of list) {
      try { await fn(o); ok += 1; } catch (e) { toast.error(`${o.name}: ${formatApiErrorDetail(e.response?.data?.detail) || "failed"}`); }
    }
    setBusy(false);
    if (ok) toast.success(okMsg(ok));
    setPendingDelete([]);
    refresh();
  };
  const doDelete = () => run(pendingDelete, (o) => api.delete(`/users/${o.user_id}`), (n) => `Deleted ${n} account${n === 1 ? "" : "s"}`);
  const suggested = (items || []).filter((o) => o.suggestion);
  const applySuggested = () => run(suggested, (o) => api.post(`/user-orphans/${o.user_id}/assign`, { role: o.suggestion.role, organization_id: o.suggestion.org_id }),
    (n) => `Applied ${n} suggested fix${n === 1 ? "" : "es"}`);
  const doBulkAssign = () => run(picked, (o) => api.post(`/user-orphans/${o.user_id}/assign`, { role: bulk.role, organization_id: bulk.org }),
    (n) => `Re-assigned ${n} account${n === 1 ? "" : "s"}`);

  const count = items?.length ?? 0;
  return (
    <>
      <Card className={`flex items-center justify-between gap-4 border-slate-200 p-4 shadow-sm ${count ? "border-l-4 border-l-amber-400" : ""}`} data-testid="orphan-accounts-card">
        <div className="flex items-center gap-3">
          <div className={`rounded-lg p-2 ${count ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-400"}`}><UserX className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Orphan accounts</div>
            <div className="text-xs text-slate-500" data-testid="orphan-accounts-count">
              {items === null ? "Checking…" : count ? `${count} login${count === 1 ? "" : "s"} don't belong to any partner, customer or staff role` : "Every login belongs to a partner, customer or staff role"}
            </div>
            <AlertNote lastSent={lastSent} />
          </div>
        </div>
        <Button variant="outline" size="sm" disabled={!count} onClick={() => setOpen(true)} data-testid="orphan-accounts-open">Review {count || ""}</Button>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" data-testid="orphan-accounts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Orphan accounts</DialogTitle>
            <DialogDescription>Logins with no organization, a deleted organization, or a role that no longer exists. Give each a new role and home, or delete it.</DialogDescription>
          </DialogHeader>
          {suggested.length > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-900" data-testid="orphan-suggest-bar">
              <span className="flex items-center gap-1.5"><Sparkles className="h-4 w-4" />
                {confirmAll ? `Move ${suggested.length} account${suggested.length === 1 ? "" : "s"} to their suggested home now?`
                  : `${suggested.length} account${suggested.length === 1 ? " has" : "s have"} a suggested home, already filled in below.`}</span>
              {confirmAll ? (
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setConfirmAll(false)} data-testid="orphan-apply-all-cancel">Cancel</Button>
                  <Button size="sm" disabled={busy} onClick={() => { setConfirmAll(false); applySuggested(); }} data-testid="orphan-apply-all-confirm">
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Yes, apply {suggested.length}
                  </Button>
                </div>
              ) : (
                <Button size="sm" disabled={busy} onClick={() => setConfirmAll(true)} data-testid="orphan-apply-all-suggestions">Apply all {suggested.length}</Button>
              )}
            </div>
          )}
          {sel.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2" data-testid="orphan-bulk-bar">
              <span className="text-sm text-slate-600">{sel.length} selected:</span>
              <AssignControls roles={roles} orgs={orgs} value={bulk} onChange={setBulk} testid="orphan-bulk" />
              <Button size="sm" variant="outline" disabled={!ready(roles, bulk) || busy} onClick={doBulkAssign} data-testid="orphan-bulk-assign">Assign all</Button>
              <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50" onClick={() => setPendingDelete(picked)} data-testid="orphan-bulk-delete">
                <Trash2 className="h-3.5 w-3.5" /> Delete selected
              </Button>
            </div>
          )}
          <ConfirmDelete targets={pendingDelete} busy={busy} onCancel={() => setPendingDelete([])} onConfirm={doDelete} />
          <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-200">
            <table className="w-full text-left" data-testid="orphan-accounts-table">
              <thead className="sticky top-0 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-2 pl-3"><input type="checkbox" className="h-4 w-4 accent-[#206bc4]" data-testid="orphan-pick-all"
                    checked={!!items?.length && sel.length === items.length} onChange={(e) => setSel(e.target.checked ? items.map((o) => o.user_id) : [])} /></th>
                  <th className="py-2 pr-3">Account</th><th className="py-2 pr-3">Problem</th><th className="whitespace-nowrap py-2 pr-3">Last sign-in</th><th className="py-2 pr-3">Fix</th>
                </tr>
              </thead>
              <tbody>
                {(items || []).map((o) => (
                  <OrphanRow key={o.user_id} o={o} roles={roles} orgs={orgs} checked={sel.includes(o.user_id)} focused={focus === o.user_id}
                    onCheck={() => setSel((s) => (s.includes(o.user_id) ? s.filter((x) => x !== o.user_id) : [...s, o.user_id]))}
                    onDone={refresh} onDelete={setPendingDelete} />
                ))}
                {!items?.length && <tr><td colSpan={5} className="py-8 text-center text-sm text-slate-400">No orphan accounts left.</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
